import { LitElement, html, css, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { Feature } from '../../schemas/feature.js'

@customElement('selection-tools-bar')
export class SelectionToolsBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 100%;
    }

    .back-button {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      padding: 0 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease;
    }

    .back-button:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    .selection-info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 2px;
      margin-right: 12px;
      white-space: nowrap;
    }

    .info-label {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.7);
      line-height: 1;
    }

    .info-value {
      font-size: 13px;
      font-weight: 500;
      color: white;
      line-height: 1;
    }

    .tools-group {
      display: flex;
      height: 100%;
    }

    .tool-button {
      width: 56px;
      height: 100%;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      transition: all 0.15s ease;
      padding: 0;
    }

    .tool-button:hover {
      color: white;
      background: rgba(255, 255, 255, 0.12);
    }

    .tool-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
    }

    .hint {
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      display: flex;
      align-items: center;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
    }
  `

  @property({ type: Number }) selectedCount = 0
  @property({ type: Number }) userFeatureCount = 0
  @property({ type: Number }) overlayFeatureCount = 0
  @property({ type: Boolean }) hasPlanContext = false
  @property({ type: Array }) selectedFeatures: Feature[] = []
  @property({ type: Boolean }) joinPreview = false

  private get canJoin(): boolean {
    if (this.selectedFeatures.length !== 2) return false
    const lineTypes = ['line', 'track', 'route']
    return (
      lineTypes.includes(this.selectedFeatures[0].type) &&
      lineTypes.includes(this.selectedFeatures[1].type)
    )
  }

  private startJoinPreview(): void {
    this.dispatchEvent(new CustomEvent('join-preview', { bubbles: true }))
  }

  private confirmJoin(): void {
    this.dispatchEvent(new CustomEvent('join-confirmed', { bubbles: true }))
  }

  private cancelJoin(): void {
    this.dispatchEvent(new CustomEvent('join-cancelled', { bubbles: true }))
  }

  private deleteSelected(): void {
    this.dispatchEvent(new CustomEvent('delete-selected', { bubbles: true }))
  }

  private openLayerPicker(e: Event): void {
    const button = e.currentTarget as HTMLElement
    const rect = button.getBoundingClientRect()
    this.dispatchEvent(
      new CustomEvent('open-style-editor', {
        detail: { styleProperty: 'layer', buttonRect: rect },
        bubbles: true,
      })
    )
  }

  private clearSelection(): void {
    this.dispatchEvent(new CustomEvent('selection-cleared', { bubbles: true }))
  }

  private addToPlan(): void {
    this.dispatchEvent(
      new CustomEvent('add-to-plan', {
        detail: { pickPlan: !this.hasPlanContext },
        bubbles: true,
      })
    )
  }

  private createPlanFromSelection(): void {
    this.dispatchEvent(new CustomEvent('create-plan-from-selection', { bubbles: true }))
  }

  private renderInfoValue(): unknown {
    const total = this.selectedCount
    if (total === 0) return nothing
    const user = this.userFeatureCount
    const overlay = this.overlayFeatureCount
    if (user > 0 && overlay > 0) {
      return html`${total} features (${user} drawn, ${overlay} trail${overlay !== 1 ? 's' : ''})`
    }
    return html`${total} feature${total !== 1 ? 's' : ''}`
  }

  override render(): unknown {
    if (this.selectedCount === 0) {
      return html` <div class="hint">Click or drag to select features</div> `
    }

    return html`
      <button class="back-button" @click=${this.clearSelection} title="Clear selection">
        <span class="material-icons">arrow_back</span>
      </button>

      <div class="selection-info">
        <div class="info-label">SELECTED</div>
        <div class="info-value">${this.renderInfoValue()}</div>
      </div>

      <div class="tools-group">
        ${this.userFeatureCount > 0
          ? html`
              <button class="tool-button" @click=${this.openLayerPicker} title="Move to layer">
                <span class="material-icons">layers</span>
                <span class="tool-label">Layer</span>
              </button>
              ${this.canJoin
                ? this.joinPreview
                  ? html`
                      <button class="tool-button" @click=${this.confirmJoin} title="Accept join">
                        <span class="material-icons">check</span>
                        <span class="tool-label">Accept</span>
                      </button>
                      <button class="tool-button" @click=${this.cancelJoin} title="Cancel join">
                        <span class="material-icons">close</span>
                        <span class="tool-label">Cancel</span>
                      </button>
                    `
                  : html`
                      <button
                        class="tool-button"
                        @click=${this.startJoinPreview}
                        title="Join two lines"
                      >
                        <span class="material-icons">merge</span>
                        <span class="tool-label">Join</span>
                      </button>
                    `
                : nothing}
              <button class="tool-button" @click=${this.deleteSelected} title="Delete selected">
                <span class="material-icons">delete</span>
                <span class="tool-label">Delete</span>
              </button>
            `
          : nothing}
        ${this.overlayFeatureCount > 0
          ? html`
              <button class="tool-button" @click=${this.addToPlan} title="Add to plan">
                <span class="material-icons">add_location</span>
                <span class="tool-label">To Plan</span>
              </button>
              ${!this.hasPlanContext
                ? html`
                    <button
                      class="tool-button"
                      @click=${this.createPlanFromSelection}
                      title="Create new plan"
                    >
                      <span class="material-icons">note_add</span>
                      <span class="tool-label">New Plan</span>
                    </button>
                  `
                : nothing}
            `
          : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'selection-tools-bar': SelectionToolsBar
  }
}
